import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LeaderService } from 'src/app/leader-toolkit/services/leader.service';
import { Location } from '@angular/common'; // 1. Import Location

interface PerformanceMetric {
  value: string | number;
  label: string;
  trend?: 'up' | 'down' | 'neutral';
  trendPercent?: number;
}

interface Meeting {
  id: string;
  title: string;
  date: string;
  time: string;
  meetingUrl?: string;
}

interface Resource {
  title: string;
  url: string;
  checked?: boolean;
}

interface GoalMetric {
  label: string;
  vetcorAvg: string;
  current: string;
  recommended: string;
  target: string;
  isEditing?: boolean;
}

interface Goal {
  id: string;
  name: string;
  status: 'live' | 'pending-hm' | 'pending-internal';
  progress?: number;
  progressType?: 'live' | 'pending' | 'review';
  metrics: GoalMetric[];
  description?: string;
  dueDate?: string;
  resources?: Resource[];
  assignee?: string;
  assigneeRole?: string;
  expanded?: boolean;
  requiresReview?: boolean;
  reviewReason?: string;
}

interface Hospital {
  id: string;
  name: string;
  code: string;
}

@Component({
  selector: 'vc-hospital-editor',
  templateUrl: './hospital-editor.component.html',
  styleUrl: './hospital-editor.component.scss',
  standalone: false
})
export class HospitalEditorComponent implements OnInit {
  activeTab: 'tasks' | 'scheduling' | 'goals' | 'dashboard' = 'goals';
  selectedQuarter: string = 'Q1 2025';
  quarters: string[] = ['Q1 2025', 'Q2 2025', 'Q3 2025', 'Q4 2025'];
  isEditMode: boolean = false;
  newTaskDescription: string = '';
  selectedAssignee: string = '';

  selectedHospital: Hospital = {
    id: '1',
    name: 'FL - Apopka - Park Ave',
    code: 'FL-APK'
  };

  hospitals: Hospital[] = [
    { id: '1', name: 'FL - Apopka - Park Ave', code: 'FL-APK' },
    { id: '2', name: 'FL - Orlando - Main St', code: 'FL-ORL' },
    { id: '3', name: 'TX - Austin - Downtown', code: 'TX-AUS' }
  ];

  performanceMetrics: PerformanceMetric[] = [
    { value: 23, label: 'New Patients per Week', trend: 'up', trendPercent: 10 },
    { value: 38, label: 'Service Visits per Week', trend: 'up', trendPercent: 5 },
    { value: '1.5hrs', label: 'Avg Meeting Hours per Visit', trend: 'down', trendPercent: 8 },
    { value: '$10.6k', label: 'Achieved Revenue per Week', trend: 'up', trendPercent: 12 }
  ];

  nextMeeting: Meeting = {
    id: '1',
    title: 'Next Meeting',
    date: 'Tuesday, November 25, 2025',
    time: '12:00 PM - 1:00 PM',
    meetingUrl: 'https://meet.example.com/next'
  };

  lastMeeting: Meeting = {
    id: '2',
    title: 'Last Meeting',
    date: 'Monday, November 4, 2025',
    time: '11:00 AM - 11:30 AM'
  };

  goalsLive: Goal[] = [
    {
      id: '1',
      name: 'Expand Hours',
      status: 'live',
      progress: 85,
      progressType: 'live',
      dueDate: 'End of Q1',
      expanded: true,
      metrics: [
        { label: 'VETCOR AVG', vetcorAvg: '30 hours/week', current: '35 hours/week', recommended: '38 hours/week', target: '40' }
      ],
      description: 'Extending hours a bit more will improve customer accessibility'
    },
    {
      id: '2',
      name: 'Optimize Online Booking',
      status: 'live',
      progress: 65,
      progressType: 'live',
      dueDate: 'End of Q1',
      expanded: false,
      metrics: [
        { label: 'VETCOR AVG', vetcorAvg: '22 hours/week', current: '26 hours/week', recommended: '30 hours/week', target: '32' }
      ],
      description: 'Adding a few more hours will reduce phone call volume'
    }
  ];

  goalsPendingHM: Goal[] = [
    {
      id: '3',
      name: 'Tail-O-Phone',
      status: 'pending-hm',
      progress: 45,
      progressType: 'pending',
      dueDate: 'End of Q1',
      expanded: true,
      requiresReview: true,
      reviewReason: 'HM/RM Review',
      assignee: 'Victoria Brown',
      assigneeRole: 'VETCOR RM',
      metrics: [
        { label: 'VETCOR AVG', vetcorAvg: '30 dispatches/day', current: '5 dispatches/day', recommended: '15 dispatches/day', target: '10' }
      ],
      description: 'Important! Here is a reason why the target is lower than the recommended target',
      resources: [
        { title: 'Access via vetcornow.com to view automated reminders and are past due', url: '#', checked: false },
        { title: 'PetDesk Texting Dashboard to reach out to past due pets with the unique URL', url: '#', checked: false },
        { title: "Utilize Vetcor's Two-Way Text Templates by producing commonly used messages into PetDesk", url: '#', checked: false },
        { title: 'Use the Vetterine Tracking Link to see how impactful your manual outreach is via text', url: '#', checked: false }
      ]
    }
  ];

  goalsPendingInternal: Goal[] = [
    {
      id: '4',
      name: 'Activate Paid Ads',
      status: 'pending-internal',
      progress: 30,
      progressType: 'review',
      dueDate: 'End of Q1',
      expanded: true,
      requiresReview: true,
      reviewReason: 'Internal Review',
      metrics: [
        { label: 'VETCOR AVG', vetcorAvg: '$250/week', current: '$275/week', recommended: '$270/week', target: '$270.00' }
      ]
    }
  ];
  quarterList =[];

  constructor(private router: Router,  private leaderService: LeaderService, private location: Location) {}

  ngOnInit(): void {
    this.loadHospitalData();
    this.qtrList();
  }

  loadHospitalData(): void {
    // API call to load hospital data
  }

    qtrList() {
    this.leaderService.getQtrList().subscribe((res: any) => {
       this.quarterList = res.data || [];
    });
  }

  applyQtr() {
  }

  setActiveTab(tab: 'tasks' | 'scheduling' | 'goals' | 'dashboard'): void {
    this.activeTab = tab;
    if (tab === 'tasks') {
      this.router.navigate(['/leader-toolkit/tasks']);
    } else if (tab === 'dashboard') {
      this.router.navigate(['/leader-toolkit/hospital-dashboard']);
    }
  }

  selectQuarter(quarter: string): void {
    this.selectedQuarter = quarter;
    this.loadGoalsForQuarter(quarter);
  }

  loadGoalsForQuarter(quarter: string): void {
    // API call to load goals for selected quarter
  }

  selectHospital(hospital: Hospital): void {
    this.selectedHospital = hospital;
    this.loadHospitalData();
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
  }

  toggleGoalExpand(goal: Goal): void {
    goal.expanded = !goal.expanded;
  }

  editGoalTarget(goal: Goal, metricIndex: number): void {
    goal.metrics[metricIndex].isEditing = true;
  }

  saveGoalTarget(goal: Goal, metricIndex: number): void {
    goal.metrics[metricIndex].isEditing = false;
    // Mark goal for review if target changed
    goal.requiresReview = true;
    goal.reviewReason = 'VP/DO Review Required';
  }

  deleteGoal(goal: Goal): void {
    // Remove from appropriate list
    this.goalsLive = this.goalsLive.filter(g => g.id !== goal.id);
    this.goalsPendingHM = this.goalsPendingHM.filter(g => g.id !== goal.id);
    this.goalsPendingInternal = this.goalsPendingInternal.filter(g => g.id !== goal.id);
  }

  assignNewGoal(): void {
    // Open goal assignment modal
  }

  saveGoalPlan(): void {
    // Save all goal changes
    this.isEditMode = false;
  }

  scheduleMeeting(): void {
    // Open meeting scheduler
  }

  joinMeeting(meeting: Meeting): void {
    if (meeting.meetingUrl) {
      window.open(meeting.meetingUrl, '_blank');
    }
  }

  viewMeetingNotes(meeting: Meeting): void {
    // Navigate to meeting notes
  }

  toggleResource(resource: Resource): void {
    resource.checked = !resource.checked;
  }

  addTask(goal: Goal): void {
    if (this.newTaskDescription.trim()) {
      // Add task to goal
      this.newTaskDescription = '';
      this.selectedAssignee = '';
    }
  }

  getProgressClass(progressType?: string): string {
    switch (progressType) {
      case 'live': return 'progress-live';
      case 'pending': return 'progress-pending';
      case 'review': return 'progress-review';
      default: return 'progress-live';
    }
  }
locationBack(): void {
   this.location.back();
  }

  
}